import { Box, IconButton, SimpleGrid, StyleProps } from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import { useTranslation } from "react-i18next";
import { EyeIcon } from "shared/icons/EyeIcon";
import { EyeOffIcon } from "shared/icons/EyeOffIcon";
import { useBoolean } from "usehooks-ts";

export type DescriptionsChild =
  | React.ReactElement<DescriptionsItemProps>
  | string
  | undefined
  | null;

interface DescriptionsProps extends StyleProps {
  responsiveColumn?: boolean;
  children?:
    | Array<DescriptionsChild>
    | DescriptionsChild
    | Array<Array<DescriptionsChild> | DescriptionsChild>;
}

export interface DescriptionsItemProps {
  label: React.ReactNode;
  isSensitive?: boolean;
}

const DescriptionsGridItem: React.FC<
  React.PropsWithChildren<DescriptionsItemProps>
> = ({ label, isSensitive, children }) => {
  const { t } = useTranslation(["common"]);
  const childrenText =
    typeof children === "boolean"
      ? children
        ? t("common:yes")
        : t("common:no")
      : children;

  const { value: showSensitiveValue, toggle: toggleShowSensitiveValue } =
    useBoolean();
  return (
    <>
      <Box className="mb-descriptions-item mb-descriptions-item-label">
        {label}
      </Box>
      <Box
        className="mb-descriptions-item mb-descriptions-item-value"
        data-private={dataAttr(isSensitive)}
      >
        <Box className="mb-descriptions-item-value-text">
          {isSensitive && !showSensitiveValue ? "******" : childrenText}
        </Box>
        {isSensitive && (
          <SensitiveToggleButton
            isVisible={showSensitiveValue}
            onClick={toggleShowSensitiveValue}
          />
        )}
      </Box>
    </>
  );
};

const DescriptionsGrid: React.FC<DescriptionsProps> = ({
  children,
  ...props
}) => (
  <SimpleGrid
    columns={2}
    spacingX="8px"
    alignItems="stretch"
    sx={{
      ".mb-descriptions-item": {
        p: "16px",
        display: "flex",
        alignItems: "center",
        minHeight: "28px",
        overflow: "hidden",
        "&:nth-of-type(-n+2)": {
          borderTopRadius: "6px",
        },
        "&:nth-last-of-type(-n+2)": {
          borderBottomRadius: "6px",
        },
      },
      ".mb-descriptions-item-label": {
        textStyle: "smallBold",
        bg: "gray.100",
      },
      ".mb-descriptions-item-value": {
        bg: "gray.50",
        justifyContent: "space-between",
      },
      ".mb-descriptions-item-value-text": {
        // ensure that the value is always at least 1 line tall
        _after: {
          content: '"\\200b"', // zero-width space
          visibility: "hidden",
        },
      },
    }}
    {...props}
  >
    {children}
  </SimpleGrid>
);

const Descriptions = DescriptionsGrid as typeof DescriptionsGrid & {
  Item: typeof DescriptionsGridItem;
};

Descriptions.Item = DescriptionsGridItem;

export default Descriptions;

const SensitiveToggleButton: React.FC<{
  isVisible: boolean;
  onClick: () => void;
}> = ({ isVisible, onClick }) => {
  const { t } = useTranslation(["common"]);
  const SensitiveToggleIcon = isVisible ? EyeOffIcon : EyeIcon;
  return (
    <IconButton
      aria-label={t("common:toggleSensitive")}
      variant="unstyled"
      size="xxs-icon"
      icon={
        <SensitiveToggleIcon
          w="16px"
          h="16px"
          color="gray.400"
          _active={{ color: "gray.600" }}
        />
      }
      onClick={onClick}
    />
  );
};
